import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Box,
  Chip,
  Pagination,
  CircularProgress,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { toast } from 'react-toastify';
import { getHistory, revertPage } from './seoApi';

const ACTION_COLOR = {
  publish: 'success',
  revert: 'warning',
  'draft-save': 'default',
  create: 'info',
  update: 'info',
  delete: 'error',
  enable: 'success',
  disable: 'default',
};

const LIMIT = 25;

export default function History() {
  const [params] = useSearchParams();
  const pathFilter = params.get('path') || '';
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const query = { page, limit: LIMIT };
    if (pathFilter) query.path = pathFilter;
    const res = await getHistory(query);
    if (res?.status === 200) {
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } else {
      toast.error(res?.data?.message || 'Failed to load history');
    }
    setLoading(false);
  }, [page, pathFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const revert = async (entry) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Restore "${entry.path}" to this version? This publishes it immediately.`)) return;
    const res = await revertPage(entry.path, entry._id);
    if (res?.status === 200) {
      toast.success('Reverted and published');
      load();
    } else {
      toast.error(res?.data?.message || 'Failed to revert');
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4">Change history</Typography>
          {pathFilter && (
            <Typography variant="body2" color="text.secondary">Filtered to {pathFilter}</Typography>
          )}
        </Box>
        <Button variant="outlined" onClick={() => navigate('/dashboard/seo')}>Back to pages</Button>
      </Stack>

      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell align="right">Restore</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No history yet.
                    </TableCell>
                  </TableRow>
                )}
                {entries.map((e) => (
                  <TableRow key={e._id} hover>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {new Date(e.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>{e.user?.name || e.user?.email || 'Unknown'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={e.action} color={ACTION_COLOR[e.action] || 'default'} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{e.path || '—'}</TableCell>
                    <TableCell align="right">
                      {e.entityType === 'pageMeta' && ['publish', 'revert'].includes(e.action) && (
                        <Button size="small" startIcon={<RestoreIcon />} onClick={() => revert(e)}>
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {pageCount > 1 && (
        <Stack alignItems="center" mt={3}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
        </Stack>
      )}
    </Container>
  );
}
